import {
  Button,
  Card,
  Drawer,
  showConfirmationPrompt,
  Table,
  TableBody,
  TableCell,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { useParams } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import darkEmptyFile from '~/assets/emptyStates/empty-file-dark.svg?react'
import emptyFile from '~/assets/emptyStates/empty-file.svg?react'
import { Boundary } from '~/components'
import { EmptyState, Spinner } from '~/elements'
import {
  useDeleteKnowledgeSources,
  useKnowledgeSource,
  useListWebPageSourceDsIds,
} from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useInfiniteScroll } from '~/hooks'
import { useLocalStore } from '~/stores'
import { showAddKnowledgeSourceDialog } from './AddKnowledgeSource/AddKnowledgeSourceDialog'
import { KnowledgeSourceStatus } from './KnowledgeSourceCell'
import { KnowledgeSourceDrawer } from './KnowledgeSourceDrawer'
import { KnowledgeSourceMobileCard } from './KnowledgeSourceMobileCard'
import { KnowledgeSourceSelectionProvider } from './KnowledgeSourceSelectionContext'
import { KnowledgeSourceTableHeader, KnowledgeSourceTableHeaderRow } from './KnowledgeSourceTableHeader'
import { KnowledgeSourceTableRow } from './KnowledgeSourceTableRow'
import { WebPageSourceMobileCard } from './WebPageSourceMobileCard'
import { WebPageSourceRow } from './WebPageSourceRow'

type KnowledgeSourceTableProps = {
  workspaceId: string
  botId: string
  kbId: string
  knowledgeBaseName: string
}

export const KnowledgeSourceTable = (props: KnowledgeSourceTableProps) => {
  const { workspaceId, botId, kbId, knowledgeBaseName } = props
  const [drawerKnowledgeSourceId, setDrawerKnowledgeSourceId] = useState<string | undefined>(undefined)
  const isMobile = useIsMobile()
  const { getPreferredTheme } = useLocalStore()
  const theme = getPreferredTheme()

  const { kbId: backendKbId } = useParams({ strict: false })
  const { data, fetchNextPage, hasNextPage, isFetching } = useKnowledgeSource({ workspaceId, botId, kbId })

  const knowledgeSources = data.pages.flatMap((page) => page.files)
  const dsIds = useListWebPageSourceDsIds({ workspaceId, botId, kbId })

  const { mutate: deleteKnowledgeSources } = useDeleteKnowledgeSources({ workspaceId, botId })

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  return (
    <KnowledgeSourceSelectionProvider knowledgeSources={knowledgeSources} dsIds={dsIds}>
      <div className="flex flex-col gap-4 md:gap-6">
        {/* Desktop Header */}
        <div className="hidden md:block">
          <KnowledgeSourceTableHeader
            workspaceId={workspaceId}
            botId={botId}
            kbId={kbId}
            totalItems={knowledgeSources.length + dsIds.length}
            showOrigin={true}
          />
        </div>

        {/* Mobile View (Cards) */}
        <div className="block md:hidden">
          <p className="mb-7 mt-1 w-full text-end text-sm font-medium text-foreground">
            {knowledgeSources.length + dsIds.length} Items
          </p>
          {knowledgeSources.length === 0 && dsIds.length === 0 ? (
            <div className="flex w-full items-center justify-center py-16">
              <EmptyState
                iconSize={6}
                icon={theme === 'dark' ? darkEmptyFile : emptyFile}
                className="py-16"
                title="No Data Sources Yet in your Knowledge Base"
                description="Let's add some data sources so your knowledge base can grow!"
              />
            </div>
          ) : (
            <div className="space-y-6">
              {dsIds.map((dsId) => (
                <WebPageSourceMobileCard
                  key={dsId}
                  dsId={dsId}
                  workspaceId={workspaceId}
                  botId={botId}
                  kbId={kbId}
                  backendKbId={backendKbId}
                />
              ))}
              {knowledgeSources
                .filter((source) => source.tags['dsType'] !== 'web-page')
                .map((knowledgeSource) => (
                  <KnowledgeSourceMobileCard
                    key={knowledgeSource.id}
                    knowledgeSource={knowledgeSource}
                    onClick={() => setDrawerKnowledgeSourceId(knowledgeSource.id)}
                    showOrigin={true}
                    status={<KnowledgeSourceStatus status={knowledgeSource.status} />}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Desktop View (Table) */}
        <div className="hidden md:block">
          <Card>
            <Table>
              <KnowledgeSourceTableHeaderRow showOrigin={true} />
              {knowledgeSources.length > 0 || dsIds.length > 0 ? (
                <TableBody>
                  {dsIds.map((dsId, index) => (
                    <WebPageSourceRow
                      key={dsId}
                      index={index}
                      dsId={dsId}
                      workspaceId={workspaceId}
                      botId={botId}
                      kbId={kbId}
                      backendKbId={backendKbId}
                    />
                  ))}
                  {knowledgeSources
                    .filter((source) => source.tags['dsType'] !== 'web-page')
                    .map((knowledgeSource, index) => (
                      <KnowledgeSourceTableRow
                        key={knowledgeSource.id}
                        index={dsIds.length + index}
                        knowledgeSource={knowledgeSource}
                        onClick={() => setDrawerKnowledgeSourceId(knowledgeSource.id)}
                        showOrigin={true}
                      />
                    ))}
                </TableBody>
              ) : (
                <TableCell className="w-full text-center" colSpan={6}>
                  <EmptyState
                    iconSize={6}
                    icon={theme === 'dark' ? darkEmptyFile : emptyFile}
                    className="col-[1/-1] py-16"
                    title="No Data Sources Yet in your Knowledge Base"
                    description="Let's add some data sources so your knowledge base can grow!"
                    primaryAction={
                      <Button
                        variant="ghost"
                        className="mt-4"
                        onClick={() => showAddKnowledgeSourceDialog({ workspaceId, botId, kbId })}
                      >
                        <Plus />
                        Add Knowledge Source
                      </Button>
                    }
                  />
                </TableCell>
              )}
            </Table>
          </Card>
        </div>

        <div ref={bottomRef} />
        {isFetching && hasNextPage && (
          <div className="flex w-full items-center justify-center py-4">
            <Spinner size="6" />
          </div>
        )}

        {drawerKnowledgeSourceId && (
          <Boundary>
            <Drawer
              open={drawerKnowledgeSourceId !== undefined}
              onOpenChange={() => setDrawerKnowledgeSourceId(undefined)}
              direction={isMobile ? 'bottom' : 'right'}
              modal={false}
            >
              <KnowledgeSourceDrawer
                workspaceId={workspaceId}
                botId={botId}
                kbId={kbId}
                knowledgeSourceId={drawerKnowledgeSourceId}
                knowledgeBaseName={knowledgeBaseName}
                handleDelete={() => {
                  if (drawerKnowledgeSourceId) {
                    showConfirmationPrompt('Are you sure you want to delete this knowledge source?', {
                      title: 'Delete Knowledge Source',
                      variant: 'danger',
                    }).then(() => deleteKnowledgeSources({ workspaceId, botId, kbId, ids: [drawerKnowledgeSourceId] }))
                  }
                }}
              />
            </Drawer>
          </Boundary>
        )}
      </div>
    </KnowledgeSourceSelectionProvider>
  )
}
